import { useState, useEffect, useMemo } from 'react';

interface UseScoreboardWidthDynamicProps {
    homeTeam: string | number;
    awayTeam: string | number;
    staticPadding?: number; 
    clockWidth?: number; 
    containerPadding?: number;
}

interface ScoreboardWidthResult {
    teamWidth: number;
    homeWidth: number;
    awayWidth: number;
    maxScoreboardWidth: number;
    screenWidth: number;
}

export const useScoreboardWidthDynamic = ({ 
    homeTeam, 
    awayTeam, 
    staticPadding = 64, 
    clockWidth = 0,
    containerPadding = 24
}: UseScoreboardWidthDynamicProps): ScoreboardWidthResult => {
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);

    useEffect(() => {
        const handleResize = () => {
            setScreenWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Function to calculate width based on the longest team name (for teamWidth)
    const calculateTeamWidth = (homeTeam: string | number, awayTeam: string | number) => {
        const homeTeamStr = String(homeTeam);
        const awayTeamStr = String(awayTeam);
        
        // Find the longest name    
        const maxLength = Math.max(homeTeamStr.length, awayTeamStr.length);
        
        // Calculate width based on the number of letters (approximately 6.5px per letter)
        const baseWidth = maxLength * 6.5;
        
        // Add minimum width (65px) and padding (0px)
        const minWidth = 65;
        const padding = 0;
        
        // Calculate available width: screen width minus static padding minus clock width minus container padding
        const availableWidth = screenWidth - staticPadding - clockWidth - containerPadding;
        
        // Divide available width by 2 for each team (home and away)
        const maxTeamWidth = availableWidth / 2;
        
        return Math.min(Math.max(baseWidth + padding, minWidth), maxTeamWidth);
    };

    // Function to calculate width for individual team based on its name length
    const calculateIndividualTeamWidth = (teamName: string | number) => {
        const teamStr = String(teamName);
        
        // Calculate width based on the number of letters (approximately 6.5px per letter)
        const baseWidth = teamStr.length * 6.5;
        
        // Add minimum width (65px) and padding (0px)
        const minWidth = 65;
        const padding = 0;
        
        // Calculate available width: screen width minus static padding minus clock width minus container padding
        const availableWidth = screenWidth - staticPadding - clockWidth - containerPadding;
        
        // Divide available width by 2 for each team (home and away)
        const maxTeamWidth = availableWidth / 2;
        
        return Math.min(Math.max(baseWidth + padding, minWidth), maxTeamWidth);
    };

    // Calculate width based on team names (for equal width)
    const teamWidth = useMemo(() => {
        return calculateTeamWidth(homeTeam, awayTeam);
    }, [homeTeam, awayTeam, screenWidth, staticPadding, clockWidth, containerPadding]);

    // Calculate individual widths for each team
    const homeWidth = useMemo(() => {
        return calculateIndividualTeamWidth(homeTeam);
    }, [homeTeam, screenWidth, staticPadding, clockWidth, containerPadding]);

    const awayWidth = useMemo(() => {
        return calculateIndividualTeamWidth(awayTeam);
    }, [awayTeam, screenWidth, staticPadding, clockWidth, containerPadding]);

    // Calculate maximum width of the entire scoreboard
    const maxScoreboardWidth = useMemo(() => {
        // Calculate available width: screen width minus static padding minus container padding
        const availableWidth = screenWidth - staticPadding - containerPadding;
        return Math.min(availableWidth, 300);
    }, [screenWidth, staticPadding, containerPadding]);

    return {
        teamWidth,
        maxScoreboardWidth,
        screenWidth,
        homeWidth,
        awayWidth
    };
}; 